<template>
  <div class="icon-picker-wrapper">
    <div
        class="icon-picker-trigger"
        @click="togglePicker"
        :class="{ 'picker-open': isOpen }"
    >
      <img class="selected-icon" :src="getIconPath(modelValue)"/>
      <span class="dropdown-arrow"><img src="@/assets/images/arrowdown.svg" /></span>
    </div>

    <div v-if="isOpen" class="icon-picker-backdrop" @click="closePicker"></div>

    <div v-if="isOpen" v-click-outside="closePicker" class="icon-picker-dropdown">
      <div class="picker-header">
        <h4 class="picker-title">{{$t("choose_an_icon")}}</h4>
        <button class="close-btn" @click.stop="closePicker">✕</button>
      </div>

      <div class="icon-categories">
        <button
            v-for="category in categories"
            :key="category.name"
            @click.stop="activeCategory = category.name"
            :class="{ 'active': activeCategory === category.name }"
            class="category-btn"
        >
          {{ category.label }}
        </button>
      </div>

      <div class="icon-grid">
        <div
            v-for="icon in currentCategoryIcons"
            :key="icon.id"
            @click.stop="selectIcon(icon.id)"
            class="icon-item"
            :class="{ 'selected': modelValue === icon.id }"
        >
          <img :src="icon.path">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ICON_CATEGORIES,
  getIconPath,
  DEFAULT_ICON_ID
} from '@/assets/icons.js'

export default {
  name: 'IconPicker',
  props: {
    modelValue: {
      type: String,
      default: DEFAULT_ICON_ID
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      activeCategory: 'emotions',
      categories: [
        { name: 'emotions', label: '😊' },
        // Gelecekte eklenebilecek kategoriler
        // { name: 'activities', label: '⚽' },
        // { name: 'objects', label: '💡' },
        // { name: 'nature', label: '🌿' }
      ]
    }
  },
  computed: {
    currentCategoryIcons() {
      return ICON_CATEGORIES[this.activeCategory] || [];
    }
  },
  methods: {
    togglePicker() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$nextTick(() => {
          document.body.style.overflow = 'hidden';
        });
      }
    },
    closePicker() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },
    selectIcon(iconId) {
      this.$emit('update:modelValue', iconId);
      this.closePicker();
    },
    // Merkezi fonksiyonu kullan
    getIconPath
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = (event) => {
          // Check if clicked element is the trigger button or its children
          const trigger = el.parentElement.querySelector('.icon-picker-trigger');
          if (trigger && trigger.contains(event.target)) {
            return;
          }

          // Check if clicked outside the dropdown
          if (!(el === event.target || el.contains(event.target))) {
            binding.value();
          }
        };

        // Add a small delay to avoid conflicts with the trigger click
        setTimeout(() => {
          document.addEventListener('click', el.clickOutsideEvent);
        }, 100);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
.icon-picker-wrapper {
  position: relative;
  display: inline-block;
}

/* Trigger Button */
.icon-picker-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 80px;
  padding: 8px 12px;
  border: 1px solid #E4E7EC;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  gap: 8px;
}

.icon-picker-trigger:hover {
  border-color: #98A2B3;
}

.icon-picker-trigger.picker-open {
  border-color: #4A68F8;
  box-shadow: 0 0 0 3px rgba(74, 104, 248, 0.1);
}

.selected-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.dropdown-arrow {
  font-size: 12px;
  color: #667085;
  transition: transform 0.2s ease;
}

.picker-open .dropdown-arrow {
  transform: rotate(180deg);
}

/* Mobile Backdrop */
.icon-picker-backdrop {
  display: none;
}

/* Dropdown */
.icon-picker-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  width: 300px;
  max-width: calc(100vw - 32px);
  background: white;
  border: 1px solid #E4E7EC;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(16, 24, 40, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Header */
.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #EAECF0;
  background: #F9FAFB;
}

.picker-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.close-btn {
  display: none;
  background: none;
  border: none;
  font-size: 18px;
  color: #667085;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  transition: all 0.2s ease;
}

.close-btn:hover {
  color: #344054;
  background: #F2F4F7;
  border-radius: 6px;
}

/* Categories */
.icon-categories {
  display: flex;
  gap: 4px;
  padding: 12px;
  border-bottom: 1px solid #EAECF0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  justify-content: space-between;
}

.icon-categories::-webkit-scrollbar {
  display: none;
}

.category-btn {
  padding: 8px 10px;
  border: 1px solid transparent;
  background: #F2F4F7;
  border-radius: 8px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.category-btn:hover {
  background: #E4E7EC;
}

.category-btn.active {
  background: #EFF4FF;
  border-color: #4A68F8;
}

/* Icon Grid */
.icon-grid {
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  overflow-y: auto;
  max-height: 240px;
  -webkit-overflow-scrolling: touch;
}

.icon-grid::-webkit-scrollbar {
  width: 6px;
}

.icon-grid::-webkit-scrollbar-track {
  background: #F2F4F7;
}

.icon-grid::-webkit-scrollbar-thumb {
  background: #D0D5DD;
  border-radius: 3px;
}

.icon-item {
  display: flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 8px;
}

.icon-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.icon-item:hover {
  background: #F2F4F7;
  transform: scale(1.1);
}

.icon-item.selected {
  background: #EFF4FF;
  box-shadow: inset 0 0 0 2px #4A68F8;
}

/* Mobile Styles */
@media (max-width: 640px) {
  .icon-picker-backdrop {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9998;
  }

  .icon-picker-dropdown {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    border-radius: 12px 12px 0 0;
    max-height: 70vh;
  }

  .close-btn {
    display: block;
  }

  .picker-header {
    padding: 16px;
  }

  .icon-grid {
    grid-template-columns: repeat(7, 1fr);
    padding: 16px;
    max-height: calc(70vh - 140px);
  }

  .icon-item {
    padding: 10px;
  }
}
</style>